import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Globe, MapPin, Compass } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Destinations() {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);
  const [settings, setSettings] = useState({
    custom_trips_title: "¿No encuentras tu viaje ideal?",
    custom_trips_subtitle: "Diseñamos tu itinerario 100% a medida.",
    custom_trips_button: "Solicitar Presupuesto"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [destRes, settingsRes, pageRes] = await Promise.all([
          axios.get(`${API}/destinations`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/pages/destinos?t=${Date.now()}`)
        ]);
        
        // Filter and the backend already sends them sorted by continent/order
        const activeDestinations = destRes.data.filter(d => d.is_active !== false);
          
        setDestinations(activeDestinations);
        setPageData(pageRes.data);
        
        if (settingsRes.data) {
          setSettings(prev => ({ ...prev, ...settingsRes.data }));
        }
      } catch (err) {
        console.error('Error fetching destinations:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.seo_title || "Destinos | Viajes de Ensueño";
      const description = pageData.seo_description || "Explora nuestra selección exclusiva de destinos por todo el mundo.";
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    }
  }, [pageData]);

  return (
    <Layout>
      {/* Hero Section Dynamic */}
      <section className="relative pt-32 pb-20 bg-primary overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={pageData?.hero_image ? `${process.env.REACT_APP_BACKEND_URL}${pageData.hero_image}` : "https://images.unsplash.com/photo-1488646953014-85cb44e25828?q=80&w=2000"} 
            alt={pageData?.title || "Nuestros Destinos"} 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-serif text-white mb-6">
            {pageData?.title || "Nuestros Destinos"}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            {pageData?.subtitle || "Explora nuestra selección exclusiva de destinos por todo el mundo. Guías detalladas, consejos de expertos y experiencias que cambian la vida."}
          </p>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
            </div>
          ) : (
            <div className="space-y-16">
              {Object.entries(
                destinations.reduce((acc, dest) => {
                  const continent = dest.continent || "Otros";
                  if (!acc[continent]) acc[continent] = [];
                  acc[continent].push(dest);
                  return acc;
                }, {})
              ).map(([continent, items]) => (
                <div key={continent}>
                  <h2 className="text-3xl font-serif text-primary mb-10 pb-4 border-b border-slate-100 inline-block px-4">
                    {continent}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {items.map((dest) => (
                      <Link 
                        key={dest.slug} 
                        to={`/destinos/${dest.slug}`}
                        className="group relative h-[450px] rounded-[2.5rem] overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
                      >
                        <img 
                          src={dest.image_url || 'https://images.unsplash.com/photo-1436491865332-7a61a109c0f2?w=800'} 
                          alt={dest.name} 
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent" />
                        
                        <div className="absolute top-6 left-6 flex gap-2">
                          <div className="bg-white/20 backdrop-blur-md px-4 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-widest border border-white/30 flex items-center gap-2">
                            <Compass className="w-3 h-3" />
                            {continent}
                          </div>
                        </div>

                        <div className="absolute bottom-8 left-8 right-8">
                          <h3 className="text-3xl font-serif text-white mb-3">{dest.name}</h3>
                          <p className="text-slate-200 text-sm line-clamp-2 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            {dest.description || "Descubre los secretos mejor guardados y vive una experiencia inolvidable."}
                          </p>
                          <div className="flex items-center gap-2 text-accent text-sm font-bold transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                            Ver Guía Completa <ArrowRight className="w-4 h-4" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Viajes a Medida Section - Moved from Home */}
      <section className="py-24 bg-slate-900 overflow-hidden relative">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-accent/20 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-50"></div>
        
        <div className="container-custom relative z-10 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-8">
              {settings.custom_trips_title || "¿No encuentras tu viaje ideal?"}
            </h2>
            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              {settings.custom_trips_subtitle || "Si ninguna de nuestras propuestas encaja exactamente con lo que buscas, no te preocupes. Somos especialistas en diseñar itinerarios exclusivos adaptados a tus deseos."}
            </p>
            <Link 
              to="/tipos-viajes/medida"
              className="inline-flex items-center gap-3 px-10 py-5 bg-accent text-primary font-bold rounded-full hover:bg-accent/90 transition-all shadow-xl hover:shadow-accent/20 hover:scale-105"
            >
              {settings.custom_trips_button || "Solicitar Viaje a Medida"}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
